import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SelfSignDocument from "./SelfSignDocument";
import SelfSignConfigure from "./SelfSignConfigure";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import { useLoading } from "../contexts/LoadingContext";

const SelfSignDocumentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingFlow, setSigningFlow] = useState(false);
  const [dataForSign, setDataForSign] = useState(null);
  const [otherData, setOtherData] = useState(null);
  const { user } = useAuth();
  const { showLoader, hideLoader } = useLoading();
  const [myInitial, setMyInitial] = useState(null);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    api.get(`/user/me/signature`).then((res) => {
      setSignature(res?.data?.data?.signature_base64);
      setMyInitial(res?.data?.data?.initials_base64);
    });
  }, [user]);
  useEffect(() => {
    const filePath = searchParams.get("filePath");
    const otherData = searchParams.get("otherData");
    setOtherData(JSON.parse(otherData));

    if (filePath) {
      // Create template data with the uploaded file path
      const template = {
        documentId: {
          filePath: filePath,
        },
        recipients: [
          {
            contactId: "self",
            email: "self",
            name: "Self",
            color: "bg-blue-500",
            isCC: false,
            sequence: 1,
            role: "Self",
            Id: "self",
            _id: "self",
            placeHolder: [],
          },
        ],
      };
      setTemplateData(template);
    }
    setLoading(false);
  }, [searchParams]);

  const continueSignCallBack = (data) => {
    setDataForSign(data);
    setSigningFlow(true);
  };

  const handleSaveDocument = async (data) => {
    showLoader();
    const response = await api.post(
      "/documents/upload/bytes",
      { data: data.pdfBytes, signRequestId: user?.id },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const result = response.data?.filePath;

    const payload = {
      filePath: otherData.filePath,
      fileName: otherData.fileName,
      originalFileName: otherData.fileName,
      fileSize: otherData.fileSize,
      contentType: otherData.contentType,
      uploadUrl: result,
      s3Key: otherData.s3Key,
    };
    await api.post("/user/self-signed-documents", payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Create a blob from the PDF bytes and download it
    // const blob = new Blob([data.pdfBytes], { type: "application/pdf" });
    // const url = window.URL.createObjectURL(blob);
    // const link = document.createElement("a");
    // link.href = url;
    // link.download = `signed-${otherData.fileName}.pdf`;
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
    // window.URL.revokeObjectURL(url);

    // toast.success("Document downloaded successfully!");
    hideLoader();
    // Navigate back to the main SelfSign page
    navigate("/dashboard/self-sign");
  };

  const handleBack = () => {
    navigate("/dashboard/self-sign");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            No Document Found
          </h2>
          <p className="text-gray-600 mb-4">
            The document you're looking for doesn't exist.
          </p>
          <button
            onClick={handleBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go Back"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {!signingFlow ? "Configure Document" : "Sign Document"}
              </h1>
              <p className="text-sm text-gray-500">
                {!signingFlow
                  ? "Add signature fields and configure your document"
                  : "Sign your document and download"}
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  !signingFlow
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                1
              </div>
              <span
                className={`text-sm ${
                  !signingFlow ? "text-blue-600 font-medium" : "text-gray-500"
                }`}
              >
                Configure
              </span>
            </div>

            <div className="w-8 h-px bg-gray-300"></div>

            <div className="flex items-center space-x-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  signingFlow
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                2
              </div>
              <span
                className={`text-sm ${
                  signingFlow ? "text-blue-600 font-medium" : "text-gray-500"
                }`}
              >
                Sign & Download
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Document Configuration or Signing Component */}
      <div className="h-[calc(100vh-80px)]">
        {!signingFlow && templateData && (
          <SelfSignConfigure
            templateUseData={templateData}
            continueSignCallBack={continueSignCallBack}
          />
        )}
        {signingFlow && dataForSign && (
          <SelfSignDocument
            templateData={dataForSign}
            onSave={handleSaveDocument}
            defaultInitials={myInitial}
            defaultSignature={signature}
          />
        )}
      </div>
    </div>
  );
};

export default SelfSignDocumentPage;
